import { useState } from 'react'

import dayjs from 'dayjs'

import {
  useSuggestCorrectionApproveApi,
  useSuggestCorrectionGenerateApi,
  useSuggestCorrectionRejectApi,
  useSuggestCorrectionsGetApi,
  useSuggestDictBuildsGetApi,
  useSuggestDictRebuildApi,
  useSuggestManualKeywordCreateApi,
  useSuggestManualKeywordDeleteApi,
  useSuggestManualKeywordsGetApi,
  useSuggestSynonymCreateApi,
  useSuggestSynonymDeleteApi,
  useSuggestSynonymsGetApi,
  useSuggestZeroHitGetApi
} from '@/common/api/modules/searchSuggest'
import { useSiteId } from '@/common/app/site'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

const DICTIONARY_TABS = [
  { key: 'master', label: 'マスター連携' },
  { key: 'syn', label: 'シノニム' },
  { key: 'corr', label: '補正辞書' },
  { key: 'manual', label: '手動キーワード' },
  { key: 'zero', label: '0件キーワード' }
] as const

type DictionaryTabKey = (typeof DICTIONARY_TABS)[number]['key']

const textInput =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-3 py-2'

// --- マスター連携（ビルド + 履歴） ---
const MasterPanel = ({ siteId }: { siteId: number }) => {
  const [started, setStarted] = useState(false)
  const { data: builds, mutate } = useSuggestDictBuildsGetApi({ site_id: siteId })
  const { trigger, isMutating } = useSuggestDictRebuildApi({
    onSuccess: () => {
      setStarted(true)
      mutate()
    }
  })
  return (
    <>
      <Area>
        <Heading>マスターから辞書ビルド</Heading>
        <AreaContent>
          <Text size={12} color={TextColor.SECONDARY}>
            商品名・記事タイトルを Sudachi
            で形態素分割・正規化し、受注/行動履歴由来の重みを付与して辞書を構築します（型番は補正対象外）。大規模カタログでも止まらないよう、ビルドはバックグラウンドで実行されます。
          </Text>
          <div className='mt-3 flex items-center gap-3'>
            <Button
              color={ButtonColor.PRIMARY}
              onClick={() => trigger({ site_id: siteId })}
              loading={isMutating}
            >
              フルビルド実行
            </Button>
            {started && (
              <Text size={14} color={TextColor.SECONDARY}>
                ビルドを開始しました。進捗は下の「ビルド履歴」で確認できます。
              </Text>
            )}
          </div>
        </AreaContent>
      </Area>
      <Area>
        <Heading>ビルド履歴</Heading>
        <AreaContent>
          {builds && builds.length > 0 ? (
            <Table>
              <thead>
                <TableRow head>
                  <TableHeadItem>
                    <Text size={12}>種別</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>状態</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>語数</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>完了時刻</Text>
                  </TableHeadItem>
                </TableRow>
              </thead>
              <tbody>
                {builds.map((b) => (
                  <TableRow key={b.id}>
                    <TableRowItem>
                      <Text>{b.mode === 'full' ? 'フル' : '増分'}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text
                        color={
                          b.status === 'complete'
                            ? TextColor.SUCCESS
                            : b.status === 'error'
                              ? TextColor.CRITICAL
                              : TextColor.SECONDARY
                        }
                      >
                        {b.status === 'complete'
                          ? '成功'
                          : b.status === 'error'
                            ? 'エラー'
                            : '実行中'}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text>{b.term_count.toLocaleString()}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text>
                        {b.finished_at ? dayjs(b.finished_at).format('YYYY/MM/DD HH:mm') : '-'}
                      </Text>
                    </TableRowItem>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          ) : (
            <Text color={TextColor.SECONDARY}>ビルド履歴はまだありません。</Text>
          )}
        </AreaContent>
      </Area>
    </>
  )
}

// --- シノニム ---
const SynonymPanel = ({ siteId }: { siteId: number }) => {
  const { data, mutate } = useSuggestSynonymsGetApi({ site_id: siteId })
  const [base, setBase] = useState('')
  const [words, setWords] = useState('')
  const { trigger: create, isMutating } = useSuggestSynonymCreateApi({
    onSuccess: () => {
      setBase('')
      setWords('')
      mutate()
    }
  })
  const { trigger: remove } = useSuggestSynonymDeleteApi({ onSuccess: () => mutate() })
  const add = () => {
    if (!base.trim()) return
    create({
      site_id: siteId,
      base_term: base.trim(),
      synonyms: words
        .split(',')
        .map((w) => w.trim())
        .filter(Boolean)
    })
  }
  return (
    <Area>
      <Heading>シノニム・類似語</Heading>
      <AreaContent>
        <Text size={12} color={TextColor.SECONDARY}>
          正規化で吸収できない同義語・略語を手動管理します（例: ワンピース ← OP, ワンピ）。
        </Text>
        <div className='mt-3 flex flex-wrap items-center gap-2'>
          <input
            className={textInput}
            placeholder='代表語'
            value={base}
            onChange={(e) => setBase(e.target.value)}
          />
          <input
            className={`${textInput} min-w-[260px]`}
            placeholder='同義語（カンマ区切り）'
            value={words}
            onChange={(e) => setWords(e.target.value)}
          />
          <Button color={ButtonColor.PRIMARY} onClick={add} loading={isMutating}>
            追加
          </Button>
        </div>
        <div className='mt-4'>
          {data && data.length > 0 ? (
            <Table>
              <thead>
                <TableRow head>
                  <TableHeadItem>
                    <Text size={12}>代表語</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>同義語</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>操作</Text>
                  </TableHeadItem>
                </TableRow>
              </thead>
              <tbody>
                {data.map((s) => (
                  <TableRow key={s.id}>
                    <TableRowItem>
                      <Text weight='bold'>{s.base_term}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text>{s.synonyms.join('、')}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Button
                        color={ButtonColor.TERTIARY}
                        onClick={() => remove({ site_id: siteId, synonym_id: s.id })}
                      >
                        削除
                      </Button>
                    </TableRowItem>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          ) : (
            <Text color={TextColor.SECONDARY}>登録済みのシノニムはありません。</Text>
          )}
        </div>
      </AreaContent>
    </Area>
  )
}

// --- 補正辞書（承認キュー） ---
const CorrectionPanel = ({ siteId }: { siteId: number }) => {
  const { data, mutate } = useSuggestCorrectionsGetApi({ site_id: siteId, status: 'pending' })
  const { trigger: approve } = useSuggestCorrectionApproveApi({ onSuccess: () => mutate() })
  const { trigger: reject } = useSuggestCorrectionRejectApi({ onSuccess: () => mutate() })
  const { trigger: generate, isMutating } = useSuggestCorrectionGenerateApi({
    onSuccess: () => mutate()
  })
  return (
    <Area>
      <Heading>補正辞書 承認キュー</Heading>
      <AreaContent>
        <div className='flex items-center justify-between'>
          <Text size={12} color={TextColor.SECONDARY}>
            「0件→直後に成功」した検索ペアから誤→正候補を生成。承認すると「もしかして」に反映されます。
          </Text>
          <Button
            color={ButtonColor.SECONDARY}
            onClick={() => generate({ site_id: siteId })}
            loading={isMutating}
          >
            候補を生成
          </Button>
        </div>
        <div className='mt-4'>
          {data && data.length > 0 ? (
            <Table>
              <thead>
                <TableRow head>
                  <TableHeadItem>
                    <Text size={12}>誤</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>正</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>信頼度</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>操作</Text>
                  </TableHeadItem>
                </TableRow>
              </thead>
              <tbody>
                {data.map((c) => (
                  <TableRow key={c.id}>
                    <TableRowItem>
                      <Text>{c.wrong}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text weight='bold'>→ {c.right}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text color={c.confidence >= 0.9 ? TextColor.SUCCESS : TextColor.WARNING}>
                        {c.confidence.toFixed(2)}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <div className='flex gap-2'>
                        <Button
                          color={ButtonColor.TERTIARY}
                          onClick={() => reject({ site_id: siteId, correction_id: c.id })}
                        >
                          却下
                        </Button>
                        <Button
                          color={ButtonColor.PRIMARY}
                          onClick={() => approve({ site_id: siteId, correction_id: c.id })}
                        >
                          承認
                        </Button>
                      </div>
                    </TableRowItem>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          ) : (
            <Text color={TextColor.SECONDARY}>承認待ちはありません。</Text>
          )}
        </div>
      </AreaContent>
    </Area>
  )
}

// --- 手動キーワード ---
const ManualPanel = ({ siteId }: { siteId: number }) => {
  const { data, mutate } = useSuggestManualKeywordsGetApi({ site_id: siteId })
  const [keyword, setKeyword] = useState('')
  const [url, setUrl] = useState('')
  const { trigger: create, isMutating } = useSuggestManualKeywordCreateApi({
    onSuccess: () => {
      setKeyword('')
      setUrl('')
      mutate()
    }
  })
  const { trigger: remove } = useSuggestManualKeywordDeleteApi({ onSuccess: () => mutate() })
  const add = () => {
    if (!keyword.trim()) return
    create({ site_id: siteId, keyword: keyword.trim(), target_url: url.trim(), pinned: true })
  }
  return (
    <Area>
      <Heading>手動キーワード</Heading>
      <AreaContent>
        <Text size={12} color={TextColor.SECONDARY}>
          プッシュしたい語をピン留めし、誘導先URLを指定します。
        </Text>
        <div className='mt-3 flex flex-wrap items-center gap-2'>
          <input
            className={textInput}
            placeholder='キーワード'
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <input
            className={`${textInput} min-w-[220px]`}
            placeholder='誘導先URL（例: /sale）'
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Button color={ButtonColor.PRIMARY} onClick={add} loading={isMutating}>
            追加
          </Button>
        </div>
        <div className='mt-4'>
          {data && data.length > 0 ? (
            <Table>
              <thead>
                <TableRow head>
                  <TableHeadItem>
                    <Text size={12}>キーワード</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>誘導先</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>操作</Text>
                  </TableHeadItem>
                </TableRow>
              </thead>
              <tbody>
                {data.map((m) => (
                  <TableRow key={m.id}>
                    <TableRowItem>
                      <Text weight='bold'>📌 {m.keyword}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text>{m.target_url || '-'}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Button
                        color={ButtonColor.TERTIARY}
                        onClick={() => remove({ site_id: siteId, keyword_id: m.id })}
                      >
                        削除
                      </Button>
                    </TableRowItem>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          ) : (
            <Text color={TextColor.SECONDARY}>登録済みの手動キーワードはありません。</Text>
          )}
        </div>
      </AreaContent>
    </Area>
  )
}

// --- 0件キーワード ---
const ZeroHitPanel = ({ siteId }: { siteId: number }) => {
  const { data, mutate } = useSuggestZeroHitGetApi({ site_id: siteId })
  const { trigger: createSynonym } = useSuggestSynonymCreateApi({})
  const registerSynonym = (keyword: string) => {
    createSynonym({ site_id: siteId, base_term: keyword, synonyms: [] })
    mutate()
  }
  return (
    <Area>
      <Heading>0件キーワード</Heading>
      <AreaContent>
        <Text size={12} color={TextColor.SECONDARY}>
          候補が出せなかった検索語です。シノニム登録や辞書改善で取りこぼしを減らします。
        </Text>
        <div className='mt-4'>
          {data && data.length > 0 ? (
            <Table>
              <thead>
                <TableRow head>
                  <TableHeadItem>
                    <Text size={12}>キーワード</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>回数</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>直近</Text>
                  </TableHeadItem>
                  <TableHeadItem>
                    <Text size={12}>操作</Text>
                  </TableHeadItem>
                </TableRow>
              </thead>
              <tbody>
                {data.map((z) => (
                  <TableRow key={z.keyword}>
                    <TableRowItem>
                      <Text>{z.keyword}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text>{z.count.toLocaleString()}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text>{z.last_at ? dayjs(z.last_at).format('YYYY/MM/DD HH:mm') : '-'}</Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Button
                        color={ButtonColor.SECONDARY}
                        onClick={() => registerSynonym(z.keyword)}
                      >
                        シノニム登録
                      </Button>
                    </TableRowItem>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          ) : (
            <Text color={TextColor.SECONDARY}>0件キーワードはありません。</Text>
          )}
        </div>
      </AreaContent>
    </Area>
  )
}

const SearchSuggestDictionaryPage = () => {
  const siteId = useSiteId()
  const [tab, setTab] = useState<DictionaryTabKey>('master')

  return (
    <Main type={MainLayoutType.MEDIUM}>
      <PageTitle
        title='辞書管理'
        description='商品・記事マスターから Sudachi で辞書を投入し、シノニム・補正・手動キーワードを運用します。'
      />
      <Content>
        <Area noBorder>
          <TabList border>
            {DICTIONARY_TABS.map((t) => (
              <Tab key={t.key} selected={tab === t.key} onClick={() => setTab(t.key)}>
                {t.label}
              </Tab>
            ))}
          </TabList>
        </Area>

        {tab === 'master' && <MasterPanel siteId={siteId} />}
        {tab === 'syn' && <SynonymPanel siteId={siteId} />}
        {tab === 'corr' && <CorrectionPanel siteId={siteId} />}
        {tab === 'manual' && <ManualPanel siteId={siteId} />}
        {tab === 'zero' && <ZeroHitPanel siteId={siteId} />}
      </Content>
    </Main>
  )
}

export default SearchSuggestDictionaryPage
